/**
 * Copyright (c) 2025-2026 eBUCIS, LLC. All rights reserved.
 *
 * Insufficient Privileges Modal - Modal for handling permission issues
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import React from 'react'
import { ShieldX, ArrowLeft } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import ModalPortal from './ModalPortal'

const DEFAULT_MESSAGE =
  'You do not have permission to access this resource. Please contact your administrator if you believe this is an error.'

/**
 * Only ever render a string. The API has two error envelopes and a structured one carries
 * `error: { code, message }`; if a non-string reaches here React throws "Objects are not valid as a
 * React child" and takes the whole app down over a permissions message. The interceptor normalizes
 * this already, so this is a backstop, not the fix.
 */
const toDisplayMessage = (message) => {
  if (typeof message === 'string' && message.trim()) return message
  if (message && typeof message === 'object' && typeof message.message === 'string') {
    return message.message
  }
  return DEFAULT_MESSAGE
}

const InsufficientPrivilegesModal = ({ isOpen, onClose, message }) => {
  const navigate = useNavigate()

  const handleGoBack = () => {
    onClose()
    navigate(-1) // Go back to previous page
  }

  const handleGoToDashboard = () => {
    onClose()
    navigate('/dashboard', { replace: true })
  }

  if (!isOpen) return null

  return (
    <ModalPortal isOpen={isOpen}>
      <div className="fixed inset-0 z-[9999] flex items-center justify-center">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black bg-opacity-50" />

        {/* Modal */}
        <div className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
          {/* Icon */}
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-orange-100 dark:bg-orange-900/20 rounded-full">
            <ShieldX className="w-8 h-8 text-orange-600 dark:text-orange-400" />
          </div>

          {/* Content */}
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Insufficient Privileges
            </h2>

            <p className="text-gray-600 dark:text-gray-400 mb-6">{toDisplayMessage(message)}</p>

            {/* Action Buttons */}
            <div className="flex flex-col space-y-3">
              <button
                onClick={handleGoToDashboard}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <span>Go to Dashboard</span>
              </button>

              <button
                onClick={handleGoBack}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Go Back</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  )
}

export default InsufficientPrivilegesModal
